import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CHARGE_FREQUENCIES, EMPTY_ALLOCATE_FORM } from "@/lib/parkingTypes";

export default function AllocateSlotDialog({ open, onOpenChange, slot, flats, settings, onSaved }) {
    const [form, setForm] = useState(EMPTY_ALLOCATE_FORM);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open || !slot) return;
        const alloc = slot.allocation;
        if (alloc) {
            setForm({
                flat_id: alloc.flat_id || "", notes: alloc.notes || "",
                charging_enabled: !!alloc.charging_enabled, charge_amount: alloc.charge_amount ?? "",
                frequency: alloc.frequency || settings?.charge_frequency || "monthly",
                charge_effective_date: alloc.charge_effective_date || "", charge_end_date: alloc.charge_end_date || "",
                grace_period_days: alloc.grace_period_days ?? "", late_fee: alloc.late_fee ?? "", charge_notes: alloc.charge_notes || "",
            });
        } else {
            setForm({
                ...EMPTY_ALLOCATE_FORM,
                charge_amount: settings?.default_charge ?? "",
                frequency: settings?.charge_frequency || "monthly",
                grace_period_days: settings?.grace_period_days ?? "",
                late_fee: settings?.late_fee ?? "",
                charge_effective_date: new Date().toISOString().slice(0, 10),
            });
        }
    }, [open, slot, settings]);

    function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

    async function handleSubmit(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                flat_id: form.flat_id, notes: form.notes || null, charging_enabled: form.charging_enabled,
                charge_amount: form.charging_enabled ? parseFloat(form.charge_amount || 0) : null,
                frequency: form.charging_enabled ? form.frequency : null,
                charge_effective_date: form.charging_enabled ? (form.charge_effective_date || null) : null,
                charge_end_date: form.charging_enabled ? (form.charge_end_date || null) : null,
                grace_period_days: form.charging_enabled && form.grace_period_days !== "" ? parseInt(form.grace_period_days, 10) : null,
                late_fee: form.charging_enabled && form.late_fee !== "" ? parseFloat(form.late_fee) : null,
                charge_notes: form.charging_enabled ? (form.charge_notes || null) : null,
            };
            await api.post(`/parking/slots/${slot.id}/allocate`, payload);
            toast.success(`Slot ${slot.slot_number} allotted`);
            onOpenChange(false);
            onSaved();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setSubmitting(false);
        }
    }

    if (!slot) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Allocate slot {slot.slot_number}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label>Flat *</Label>
                        <Select value={form.flat_id} onValueChange={(v) => set("flat_id", v)} required>
                            <SelectTrigger data-testid="allocate-flat-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Select flat" /></SelectTrigger>
                            <SelectContent>
                                {flats.map((f) => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number}{f.owner_name ? ` · ${f.owner_name}` : ""}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea data-testid="allocate-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm border-brand-line" />
                    </div>
                    <div className="border-t border-brand-line pt-4 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-brand-ink">Paid parking</Label>
                                <p className="text-xs text-brand-inkSoft">Enable to raise recurring parking charges via the Billing tab.</p>
                            </div>
                            <Switch data-testid="allocate-charging-switch" checked={form.charging_enabled} onCheckedChange={(v) => set("charging_enabled", v)} />
                        </div>
                        {form.charging_enabled && (
                            <div className="space-y-3 bg-brand-sage/20 rounded-sm p-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label>Charge amount *</Label>
                                        <Input type="number" min="0" step="0.01" required data-testid="allocate-charge-amount-input"
                                            value={form.charge_amount} onChange={(e) => set("charge_amount", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Frequency *</Label>
                                        <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                                            <SelectTrigger data-testid="allocate-frequency-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                            <SelectContent>{CHARGE_FREQUENCIES.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label>Effective from *</Label>
                                        <Input type="date" required data-testid="allocate-effective-date-input"
                                            value={form.charge_effective_date} onChange={(e) => set("charge_effective_date", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Charge end date (optional)</Label>
                                        <Input type="date" data-testid="allocate-end-date-input"
                                            value={form.charge_end_date} onChange={(e) => set("charge_end_date", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label>Grace period (days)</Label>
                                        <Input type="number" min="0" data-testid="allocate-grace-input"
                                            value={form.grace_period_days} onChange={(e) => set("grace_period_days", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Late fee (optional)</Label>
                                        <Input type="number" min="0" step="0.01" data-testid="allocate-late-fee-input"
                                            value={form.late_fee} onChange={(e) => set("late_fee", e.target.value)} className="rounded-sm border-brand-line" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Charge notes (optional)</Label>
                                    <Input data-testid="allocate-charge-notes-input" value={form.charge_notes} onChange={(e) => set("charge_notes", e.target.value)} className="rounded-sm border-brand-line" />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="allocate-cancel-btn">Cancel</Button>
                        <Button type="submit" disabled={submitting || !form.flat_id} data-testid="allocate-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            {submitting ? "Saving…" : "Allocate"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
